import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { EmptyStateTitle } from '@/shared/ui';
import {
  briefTemplatesPageDescription,
  briefTemplatesPageTitle,
} from '../../constants/briefTemplates.constants';
import { useTranslation } from 'react-i18next';

interface BriefTemplatesEmptyStateProps {
  /** Fires when the user clicks the primary "New template" CTA. */
  onCreate: () => void;
  /** Whether the viewer may create a template. False drops the CTA. */
  canCreate?: boolean;
}

/**
 * Empty state for the Brief Templates list view. Iconless + vertically
 * centered, replicating the page header (title + description) — matches the
 * Projects list empty state.
 *
 * Rendered by `BriefTemplatesList` when the templates query resolves with
 * zero items AND there is no active search term. The "no search results"
 * variant is a separate component (`BriefTemplatesNoResultsState`).
 *
 * Pure presentational. `role="status"` so screen readers announce the
 * state change.
 */
export function BriefTemplatesEmptyState(props: BriefTemplatesEmptyStateProps) {
  const { t } = useTranslation('knowledge');

  return (
    <Empty role="status" data-testid="brief-templates-empty-state" className="flex-1">
      <EmptyHeader>
        <EmptyStateTitle>{briefTemplatesPageTitle()}</EmptyStateTitle>
        <EmptyDescription>{briefTemplatesPageDescription()}</EmptyDescription>
      </EmptyHeader>
      {props.canCreate !== false && (
        <EmptyContent>
          <Button variant="default" size="default" onClick={props.onCreate}>
            <PlusIcon className="size-4" />
            {t('knowledgeHub.briefTemplatesEmptyState.newTemplate')}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}
